import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import Transition from 'react-transition-group/Transition';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ESQuestion } from '~src/interfaces/question.interface';

import {ESTopic, Topic} from '~src/interfaces/topic.interface';
import { MatrixResponse } from '~src/interfaces/heatmap.interface';
import { StoreI } from '~src/rootReducer';
import { ThunkAnyAction } from '~src/services/apiMiddleware';
import { AttributeI } from '~src/interfaces/attributes.types';
import { PeriodValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { ScrollHeader } from '~src/modules/heatmap/components/header/ScrollHeader';

import { FixedHeader, FixedHeaderTitle, GrowRow, Placeholder } from './ratingTable.sc';
import ColumnDescription from './column/ColumnDescription';
import TopicRow from './row/TopicRow';
import { hideAnimationSpeed } from './topicTitle.sc';
import QuestionRow from './row/QuestionRow';
import TableNavigator from './TableNavigator';

interface StateProps {
  result: MatrixResponse | null;
  questions: ESQuestion[];
  singleTopic: Topic | null;
  loadingTable: boolean;
}

interface OwnProps {
  topics: ESTopic[];
  selectedAttributeObj: AttributeI | null;
  selectedSurveyName: string;
  selectedTopic: string | null;
  selectTopic: (topic: string | null) => Promise<ThunkAnyAction<unknown, unknown> | void>;
  selectedPeriod: PeriodValues;
  addToRef: (el: HTMLDivElement | null) => void;
  scrollOn: (direction: 'forward' | 'back') => void;
}

type Props = OwnProps & StateProps;

const FixedHeaders: FC<Props> = (props) => {
  const [t] = useTranslation();
  const [isFixed, setIsFixed] = useState(false);
  const container = useRef<null | HTMLDivElement>(null);
  const placeholder = useRef<null | HTMLDivElement>(null);
  const listenToScroll = useCallback(() => {
    if (container.current) {
      const subHeaderPosition =
        placeholder.current && placeholder.current.getBoundingClientRect().top + window.pageYOffset;
      const rect = container.current.getBoundingClientRect();
      if (rect.top <= 5 && !isFixed) {
        setIsFixed(true);
      } else if (subHeaderPosition && window.pageYOffset < subHeaderPosition - 5 && isFixed) {
        setIsFixed(false);
      }
    }
  }, [isFixed]);

  useEffect(() => {
    document.addEventListener('scroll', listenToScroll);
    return () => {
      document.removeEventListener('scroll', listenToScroll);
    };
  }, [isFixed, listenToScroll]);

  const [selectedTopic, setSelectedTopic] = useState<string | null>(props.selectedTopic);

  useEffect(() => {
    setSelectedTopic(props.selectedTopic);
  }, [props.selectedTopic]);

  const topicClicked = (topicId: string | null) => {
    const newTopic = topicId === selectedTopic ? null : topicId;
    const selectPromise = props.selectTopic(newTopic);
    if (selectPromise) {
      selectPromise.then(() => {
        setSelectedTopic(newTopic);
      });
    }
  };

  return (
    <>
      {isFixed && <Placeholder ref={placeholder} />}
      <FixedHeader $isFixed={isFixed} ref={container}>
        {isFixed && (
          <FixedHeaderTitle data-translation="x.compare.header.title">
            <div>{t('x.compare.header.title')}</div>
            <TableNavigator scrollOn={props.scrollOn} />
          </FixedHeaderTitle>
        )}
        <ScrollHeader>
          {!isFixed && (
            <ColumnDescription
              selectedSurveyName={props.selectedSurveyName}
              selectedAttributeObj={props.selectedAttributeObj}
              scrollOn={props.scrollOn}
            />
          )}
          {props.result && (
            <TopicRow
              singleTopic={props.singleTopic}
              selectedTopic={selectedTopic}
              selectTopic={topicClicked}
              topics={props.topics}
              setContainerRef={props.addToRef}
            />
          )}
          <Transition in={!!selectedTopic} timeout={hideAnimationSpeed}>
            {status => (
              <GrowRow $show={!!selectedTopic} className={status}>
                {props.result && props.questions && !props.loadingTable && selectedTopic && (
                  <QuestionRow questions={props.questions} setContainerRef={props.addToRef} />
                )}
              </GrowRow>
            )}
          </Transition>
        </ScrollHeader>
      </FixedHeader>
    </>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  result: state.heatmap.result,
  questions: state.heatmap.questions,
  singleTopic: state.topics.currentTopic,
  loadingTable: state.heatmap.loadingTable,
});

const FixedHeadersSuper = connect(mapStateToProps)(React.memo(FixedHeaders));

export default FixedHeadersSuper;
